import {AppThunkAction} from "src/~store/models/AppThunkAction";

import { StartSignalRAction, AddMessageToStateAction, AddMessagesToStateAction, SendFileMessageAction, EditMessageAction, InitializeMessagesAction, ReadMessageAction, SetFooterCompanyTitleAction, SendMessageAction, TryGetPrevChatAction} from "./interfaces";
import { START_SIGNALR, ADD_MESSAGE_TO_STATE_SUCCESS, ADD_MESSAGES_TO_STATE_SUCCESS, EDIT_MESSAGE_SUCCESS, EDIT_MESSAGE_START, INITIALIZE_MESSAGES_SUCCESS, READ_MESSAGE, SEND_FILE_MESSAGE_START_ACTION, SEND_FILE_MESSAGE_FINISH_ACTION, SET_FOOTER_COMPANY_TITLE_SUCCESS, INIT_CLIENT_HIDE_START_MESSAGE_SUCCESS, SEND_MESSAGE_ACTION_START, SEND_MESSAGE_ACTION_SUCCESS, SEND_MESSAGE_ACTION_ERROR, TRY_GET_PREV_CHAT_START, TRY_GET_PREV_CHAT_FINISH} from "./types";
import { IMessage, IInitMessages, MessageContentType } from "../models/messages/Message";
import { FileType } from "src/app/shared/components/choose-file-dialog/ChooseFileDialog";
import { getInitMessagesAsync, getPrevChatAsync, sendFileMessageAsync, sendMessageAsync, sendPreviewUserMessageAsync } from "../../~api/actions";
import { CHAT_CLOSED_TAG, FILE_MAX_SIZE, FILE_MAX_SIZE_MESSAGE, MESSAGE_RESEND_ATTEMPTS, SEND_FILE_MESSAGE_ERROR, START_MESSAGE_TAG } from "./../models/Constants";
import { MessageType } from "../models/messages/enums/MessageType";
import { MessageEventType } from "../models/messages/enums/MessageEventType";
import { IMessageInfo } from "../models/MessageInfo";
import { IChannelMessage } from "../models/ChannelMessage";
import { IMessagePreview } from "../models/MessagePreview";
import { TryGetStartParamsFromQueryString } from "../../../shared/helpers/start-params-helper/StartParamsHelper";
import { getExternalId, getUrlParam } from "src/app/shared/helpers/url-helper/UrlParamsHelper";
import { IApplicationState } from "src/~store/models/ApplicationState";
import { SEND_PREVIEW_MESSAGE_SETTING } from "./constants";
import { serverProtocolEnum } from "../models/NegotiateResponse";
import { SEND_MESSAGE_API_ROUTE, SEND_PREVIEW_MESSAGE_API_ROUTE } from "../../~api/apiRoutes";
import { HubConnectionState } from "@microsoft/signalr";

const urlParams = new URLSearchParams(window.location.search);
const parentPage = urlParams.get('parent');
const defaultStartMode = "auto";

export const startSignalR = (
    onSignalRMessageReceived: CallableFunction): AppThunkAction<StartSignalRAction> => async dispatch => {
        dispatch({ type: START_SIGNALR, payload: { onSignalRMessageReceived } });
    };

export const addMessageToState = (message: IMessage): AppThunkAction<AddMessageToStateAction> => async dispatch => {
    dispatch({ type: ADD_MESSAGE_TO_STATE_SUCCESS, payload: { message: message } });

    if (message.contentType === MessageContentType.TextToSpeech) {
        const msg = new SpeechSynthesisUtterance();
        msg.text = message.text;
        msg.lang = 'ru-RU';
        msg.onend = () => {window.speechSynthesis.cancel()};
        window.speechSynthesis.speak(msg);
    } else if (message.contentType === MessageContentType.Navigation) {
        if (parentPage != null) 
            window.parent.postMessage({type: "redirectToUrl", url: message.text}, parentPage);
    }

    // send message to show "new message in tab name"
    if (parentPage !== null && message.messageType !== MessageType.ClientMessage) {
        window.parent.postMessage("newMessage", parentPage);
        window.onfocus = () => {
            window.parent.postMessage("focus", parentPage);
        }

        // for google analitics
        if (message.event) {
            window.parent.postMessage([MessageEventType[message.event], message.eventDescription], parentPage);
        }
    }    
}

export const getPrevChat = (): AppThunkAction<AddMessagesToStateAction|TryGetPrevChatAction> => async (dispatch, getState) => {
    dispatch({ type: TRY_GET_PREV_CHAT_START });

    try{
        const minChatId = Math.min(...(getState().chat.messages.map(m => m.chatId).filter(chId => chId != 0)));
        if(minChatId > 0) {
            const result = await getPrevChatAsync(minChatId);

            if(result.isSuccess) {
                const messages = result.value ?? [];

                dispatch({ type: ADD_MESSAGES_TO_STATE_SUCCESS, payload: { messages } });
            }
        }
    }
    catch(e){
        console.error(e);
    }

    dispatch({ type: TRY_GET_PREV_CHAT_FINISH });
}

export const addMessagesToState = (messages: IMessage[]): AppThunkAction<AddMessagesToStateAction> => async dispatch => {
    dispatch({ type: ADD_MESSAGES_TO_STATE_SUCCESS, payload: { messages } });
    // send message to show "new message in tab name"
    if (parentPage !== null && messages.length !== 0 && messages[messages.length - 1]?.messageType !== MessageType.ClientMessage) {
        window.parent.postMessage("newMessage", parentPage);
        window.onfocus = () => {
            window.parent.postMessage("focus", parentPage);
        } 

        // for google analitics
        if (messages[messages.length - 1]?.event) {
            const message = messages[messages.length - 1];
            window.parent.postMessage([MessageEventType[message.event], message.eventDescription], parentPage);
            console.log(messages[messages.length - 1].event);
        }
    }    
}

export const initializeMessages = (): AppThunkAction<InitializeMessagesAction> => async (dispatch, getState) => {
    const result = await getInitMessagesAsync();

    if(result.isSuccess) {
        const messages = result.value?.messages ?? [];
        const customerId = result.value?.customerId ?? "";
        const loadPrevChat = result.value?.loadPrevChat ?? false;

        const urlParams = new URLSearchParams(document.location.href);
        const hideClientStartMessage = urlParams.get('hideClientStartMessage')?.toLowerCase() === 'true' ?? false;
        
        dispatch({ type: INITIALIZE_MESSAGES_SUCCESS, payload: { messages, customerId: customerId, loadPrevChat } });

        dispatch({ type: INIT_CLIENT_HIDE_START_MESSAGE_SUCCESS, payload: { hideClientStartMessage } });
    }
    else {
        console.error("initializeMessages error!")
    }
}

export const trySendStartMessage = () : AppThunkAction<SendMessageAction> => async (dispatch, getState) => {
    const startMode = urlParams.get('startMode') ?? defaultStartMode;
    const {messages} = getState().chat;
    if (messages.length === 0) {

        if(startMode === defaultStartMode) {
            const firstMessage: IMessageInfo = {
                text: "/start",
                tag: START_MESSAGE_TAG,
                startParams: TryGetStartParamsFromQueryString()
            }

            sendMessage(firstMessage)(dispatch, getState);
        }
    }
}

export const sendMessage = (messageInfo: IMessageInfo, isButtonReply: boolean = false): AppThunkAction<SendMessageAction> => async (dispatch, getState) => {
    const externalId = getExternalId();
    const pushId = urlParams.get('pushId') ?? undefined;
    if(externalId) {
        let channelMessage: IChannelMessage = {
            message: messageInfo,
            from: {
                userMessengerId: externalId,
            },
            isButtonReply: isButtonReply,
        }
        if(pushId) {
            channelMessage.from.pushId = pushId;
        }
        if(window.serverProtocol === serverProtocolEnum.http) {
            dispatch({ type: SEND_MESSAGE_ACTION_START, payload: { message: {message: messageInfo, failed: false} } });
            await retrySendMessageAsync(channelMessage, 1, dispatch, getState);
        }   
        else {
            window.signalRHubConnection.invoke('MessageFromClient', channelMessage)
        }
    }
}

// For sending editing message
export const sendPreviewUserMessage = (text: string): AppThunkAction<void> => async (_, getState) => {
    const isSendPreviewMessageEnabled = getUrlParam(SEND_PREVIEW_MESSAGE_SETTING);
    if(isSendPreviewMessageEnabled == "true") {
        const messages = getState().chat.messages;
        const chatId = Math.max(...messages.map(m => m.chatId))
        if(chatId > 0) {
            const externalId = getExternalId();
            if(externalId) {
                const messagePreview: IMessagePreview = {
                    chatId,
                    text
                }
                if(window.serverProtocol === serverProtocolEnum.http){
                    await sendPreviewUserMessageAsync(messagePreview);
                }  
                else {
                    window.signalRHubConnection.invoke('PreviewUserMessage', messagePreview)
                }
            }
        }
    }
} 

export const editMessage = (message: IMessage): AppThunkAction<EditMessageAction> => async dispatch => {
    dispatch({type: EDIT_MESSAGE_START});
    dispatch({type: EDIT_MESSAGE_SUCCESS, payload: {message}});
}

export const sendFileMessage = (file: File, type: FileType): AppThunkAction<SendFileMessageAction> => async (dispatch, getState) => {
    if(file.size > FILE_MAX_SIZE) {
        alert(FILE_MAX_SIZE_MESSAGE);
        return;
    }
    dispatch({type: SEND_FILE_MESSAGE_START_ACTION});

    const result = await sendFileMessageAsync(file, type);
    if(result.isSuccess === true) {
        if(result.value) {addMessageToState(result.value)(dispatch, getState)}
    }
    else {
        alert(SEND_FILE_MESSAGE_ERROR);
    }
    dispatch({type: SEND_FILE_MESSAGE_FINISH_ACTION});
};

export const readMessage = (messageExternalId: string): AppThunkAction<ReadMessageAction> => async dispatch => {
    dispatch({ type: READ_MESSAGE, payload: {messageExternalId } });
}

export const setFooterCopmanyTitle = (): AppThunkAction<SetFooterCompanyTitleAction> => async dispatch => {
    const footerCompanyName = urlParams.get('linkName')!;
    const footerCompanyUrl = urlParams.get('linkUrl')!;
    dispatch({ type: SET_FOOTER_COMPANY_TITLE_SUCCESS, payload: { footerCompanyName, footerCompanyUrl } });
}

export const ensureGotAllMessages = (): AppThunkAction<AddMessagesToStateAction> => async (dispatch, getState) => {
    const messagesId = getState().chat.messages.map(m => m.externalId);
    const { hubConnection } = getState().chat;

    if(hubConnection.state === HubConnectionState.Connected){
        hubConnection.invoke<IMessage[]>("EnsureGotAllMessages", messagesId)
        .then(async (messages) => {
            addMessagesToState(messages)(dispatch, getState);
        })
        .catch(e => console.error(e))
    } 
}

const retrySendMessageAsync = async (message: IChannelMessage, attempt: number, dispatch: (action: SendMessageAction) => void, getState: () => IApplicationState) => {
    const messages = getState().chat.messages.filter(m => m.messageType != MessageType.ClientMessage);
    const lastOperatorMessage = messages.length > 0 ? messages
        .reduce((max, obj) => (new Date(max.timeSent).getTime() > new Date(obj.timeSent).getTime()) ? max : obj) : null;

    const isStartMessage = !lastOperatorMessage || lastOperatorMessage.tag === CHAT_CLOSED_TAG;
    
    const result = await sendMessageAsync(message, isStartMessage);

    if(result.isSuccess) {
        dispatch({ type: SEND_MESSAGE_ACTION_SUCCESS });
        return;
    }

    if(attempt >= MESSAGE_RESEND_ATTEMPTS) {
        dispatch({ type: SEND_MESSAGE_ACTION_ERROR });
        return;
    }

    setTimeout(async () => {
        retrySendMessageAsync(message, attempt + 1, dispatch, getState);
    }, fib(attempt)*1000)
}

const fib = (n: number): number => {
    return n <= 1 ? n : fib(n - 1) + fib(n - 2);
}